import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Phone, MapPin, Train, ShieldCheck, Sparkles } from "lucide-react";
import { Container, Button, Eyebrow, SectionHeading } from "@/components/primitives";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { TrustMarquee, ReviewsSection, FaqSection, CTASection } from "@/components/sections";
import { useBooking } from "@/lib/booking";
import { useSeo } from "@/lib/seo";
import { business, services, stats, gallery, trustBadges } from "@/data/site";

const heroLines = ["Elevated dentistry", "for the heart of", "Hell's Kitchen."];

const HeroImage = "https://images.unsplash.com/photo-1781513144825-aa1e284c5950?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Hero = () => {
  const { openBooking } = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden pt-32 sm:pt-40">
      <Container className="relative z-10 grid items-center gap-12 pb-24 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
            <Eyebrow>Cosmetic · Family · Restorative · Invisalign</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-heading text-[15vw] font-light leading-[0.9] tracking-tight text-[var(--fg)] sm:text-7xl lg:text-8xl">
            {heroLines.map((line, i) => (
              <span key={i} className="reveal-line">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.15 + i * 0.13, ease: [0.19, 1, 0.22, 1] }}
                >
                  {i === 2 ? <span className="italic text-[var(--primary)]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 max-w-lg text-base font-light leading-relaxed text-[var(--muted-fg)] sm:text-lg"
          >
            A modern Manhattan practice for preventative, cosmetic, restorative and orthodontic care —
            clean, calm, pain-aware and built around the schedules of busy New Yorkers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row"
          >
            <Button size="lg" onClick={() => openBooking()} data-testid="hero-book-btn">
              Book Appointment <ArrowRight size={17} />
            </Button>
            <Button size="lg" variant="outline" href={business.phoneHref} data-testid="hero-call-btn">
              <Phone size={16} /> Call / Text Us
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex items-center gap-3 text-sm text-[var(--muted-fg)]"
          >
            <MapPin size={16} className="text-[var(--primary)]" />
            {business.address1} · {business.crossStreets}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.19, 1, 0.22, 1] }}
          className="relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-[var(--border)]">
            <motion.img
              style={{ y, scale }}
              src={HeroImage}
              alt="Bright, modern dental studio reception in Hell's Kitchen, Manhattan"
              className="absolute inset-0 h-[112%] w-full object-cover"
            />
          </div>
          <div className="glass absolute -bottom-6 -left-4 flex items-center gap-4 rounded-2xl border border-[var(--border)] px-5 py-4 sm:-left-8">
            <span className="font-heading text-4xl text-[var(--primary)]">4.9★</span>
            <span className="text-xs leading-tight text-[var(--muted-fg)]">Rated by<br />1,200+ patients</span>
          </div>
          <div className="glass absolute -right-2 top-6 flex items-center gap-2 rounded-full border border-[var(--border)] px-4 py-2 sm:-right-6">
            <ShieldCheck size={16} className="text-[var(--primary)]" />
            <span className="text-xs text-[var(--fg)]">Hospital-grade sterilization</span>
          </div>
        </motion.div>
      </Container>
    </section>
  );
};

const StatsBar = () => (
  <section className="border-y border-[var(--border)] bg-[var(--surface)]">
    <Container>
      <Stagger className="grid grid-cols-2 divide-x divide-y divide-[var(--border)] sm:grid-cols-4 sm:divide-y-0">
        {stats.map((s) => (
          <StaggerItem key={s.label} className="px-4 py-10 text-center sm:py-12">
            <div className="font-heading text-4xl font-light text-[var(--fg)] sm:text-5xl">{s.value}</div>
            <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--muted-fg)]">{s.label}</div>
          </StaggerItem>
        ))}
      </Stagger>
    </Container>
  </section>
);

const ServicesPreview = () => (
  <section className="py-24 sm:py-32">
    <Container>
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading eyebrow="What we do" title="Four ways we care for your smile." className="max-w-2xl" />
        <Button to="/services" variant="ghost" className="shrink-0" data-testid="services-view-all">
          View all services <ArrowRight size={16} />
        </Button>
      </div>
      <Stagger className="mt-14 grid gap-5 sm:grid-cols-2">
        {services.map((s) => (
          <StaggerItem key={s.slug}>
            <Link
              to={`/services/${s.slug}`}
              data-testid={`home-service-${s.slug}`}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-1 transition-colors hover:border-[var(--primary)]"
            >
              <div className="relative aspect-[16/10] overflow-hidden rounded-xl">
                <img src={s.hero} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105" />
                <span className="absolute left-4 top-4 font-mono text-xs text-white/90">{s.index}</span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-2xl font-normal tracking-tight text-[var(--fg)]">{s.name}</h3>
                  <ArrowUpRight size={20} className="text-[var(--muted-fg)] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[var(--primary)]" />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[var(--muted-fg)]">{s.short}</p>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </Container>
  </section>
);

const chapters = [
  { n: "01", title: "Clean by design", body: "Hospital-grade sterilization, HEPA filtration and single-use disposables — clinical rigor you can feel the moment you sit down." },
  { n: "02", title: "Calm by default", body: "Noise-canceling headphones, weighted blankets, streaming and sedation options turn dental visits into something you don't dread." },
  { n: "03", title: "Precise by technology", body: "Digital X-rays, 3D imaging, intraoral scanners and laser dentistry mean faster, gentler, more accurate treatment." },
  { n: "04", title: "Yours by design", body: "Every smile is planned digitally and previewed with you first — so the result is exactly what you approved." },
];

const Manifesto = () => (
  <section className="bg-[var(--surface)] py-24 sm:py-32">
    <Container>
      <SectionHeading
        eyebrow="Our philosophy"
        title="Top-tier care shouldn't feel clinical or cold."
        intro="We built Meridian for New Yorkers who expect more — precision without the anxiety, luxury without the pretense."
      />
      <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--border)] sm:grid-cols-2">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.06} className="bg-[var(--bg)]">
            <div className="flex h-full flex-col p-8 sm:p-10">
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-sm text-[var(--primary)]">{c.n}</span>
                <h3 className="font-heading text-2xl font-normal tracking-tight text-[var(--fg)]">{c.title}</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[var(--muted-fg)]">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const GalleryPreview = () => {
  const feature = gallery[0];
  return (
    <section className="py-24 sm:py-32">
      <Container>
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <BeforeAfterSlider before={feature.before} after={feature.after} alt={feature.title} className="max-w-md" />
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Before & after"
              title="Real transformations. Drag to reveal."
              intro="From porcelain veneers to Invisalign and full smile makeovers — see the craft for yourself, then filter the full gallery by treatment."
            />
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/smile-gallery" data-testid="gallery-explore-btn">
                Explore Smile Gallery <Sparkles size={16} />
              </Button>
              <Button to="/services/cosmetic-dentistry" variant="outline">Cosmetic services</Button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

const Neighborhood = () => (
  <section className="bg-[var(--surface)] py-24 sm:py-32">
    <Container>
      <div className="grid gap-14 lg:grid-cols-2">
        <div>
          <SectionHeading
            eyebrow="Local & easy to reach"
            title="Right in Hell's Kitchen — minutes from the trains."
            intro="Serving patients across Hell's Kitchen, Midtown West, Chelsea, Hudson Yards and the Theater District. Iconic access, effortless commute."
          />
          <div className="mt-8 space-y-3">
            {business.transit.map((t) => (
              <div key={t} className="flex items-start gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg)] px-5 py-4">
                <Train size={18} className="mt-0.5 shrink-0 text-[var(--primary)]" />
                <span className="text-sm text-[var(--fg)]/85">{t}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button href={business.directionsHref} target="_blank" rel="noopener noreferrer" variant="outline" data-testid="home-directions-btn">
              <MapPin size={16} /> Get Directions
            </Button>
            <Button to="/contact" variant="ghost">Contact & hours <ArrowRight size={16} /></Button>
          </div>
        </div>
        <Reveal delay={0.1}>
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-[var(--border)]">
            <img
              src="https://images.unsplash.com/photo-1712076288648-6374bcfcb9a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Hell's Kitchen, Manhattan streetscape near Meridian Dental Studio"
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div className="glass absolute bottom-5 left-5 right-5 flex flex-wrap gap-2">
              {business.serviceAreas.slice(0, 5).map((a) => (
                <span key={a} className="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1 text-xs text-[var(--fg)]">{a}</span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </Container>
  </section>
);

export default function Home() {
  useSeo({
    title: "Meridian Dental Studio | Cosmetic & Family Dentist, Hell's Kitchen NYC",
    description: "Modern cosmetic, preventative, restorative & Invisalign dental care in Hell's Kitchen, Manhattan. Clean, calm, state-of-the-art. Book your appointment today.",
    path: "/",
  });
  return (
    <>
      <Hero />
      <StatsBar />
      <ServicesPreview />
      <Manifesto />
      <GalleryPreview />
      <TrustMarquee />
      <Neighborhood />
      <ReviewsSection />
      <FaqSection limit={5} showAllLink title="Questions New Yorkers ask us." />
      <CTASection />
    </>
  );
}
